import { Dialog, DialogPortal, DialogOverlay, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useMemo, useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import useSWR from 'swr';
import { api } from '../../lib/api';
import { format, isPast, addMinutes, formatDistanceToNow } from 'date-fns';
import { Loader2, CheckCircle2, PlayCircle, StopCircle, CalendarDays, Truck, UserX, Settings, AlertCircle, History, Check, UserPlus, ShieldOff, Wrench, XCircle, ChevronDown, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { getDockDisplayStatus, toManualSelectValue } from '../../lib/dock-utils';
import { dockScheduleKey, refreshDockViews } from '../../lib/swrKeys';
import { dockMutations } from '../../lib/dockMutations';
import toast from 'react-hot-toast';
import { showCenteredCheckoutError } from '../../lib/toastHelpers';
import { useAuthStore } from '../../hooks/useAuth';
import type { BlackoutWindow } from './BlackoutList';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';

// --- Exception Section Component ---
function ExceptionSection({ dock }: { dock: Dock }) {
    const [isEditing, setIsEditing] = useState(false);
    const [note, setNote] = useState(dock.exceptionNote || '');
    const [reason, setReason] = useState(dock.exceptionReason || '');
    const [ownerId, setOwnerId] = useState(dock.exceptionOwnerUserId || '');
    const [historyOpen, setHistoryOpen] = useState(false);

    // Fetch Users (for owner dropdown) - caching is handled by SWR
    const { data: userData } = useSWR(isEditing ? '/api/users' : null, () => api.getUsers());
    const users = userData?.users || [];

    // Fetch History
    const { data: historyData } = useSWR(
        historyOpen ? `/docks/${dock.id}/exception/events` : null,
        () => api.getDockExceptionEvents(dock.id)
    );
    const events = historyData?.events || [];

    const hasActiveException = !dock.exceptionResolvedAt && (dock.exceptionNote || dock.exceptionReason || dock.exceptionOwnerUserId);

    const handleSave = async () => {
        try {
            await dockMutations.updateDockException(dock.id, {
                note: note || undefined,
                reason: reason || undefined,
                ownerUserId: ownerId || null,
            });
            setIsEditing(false);
        } catch (e) { } // Toast handled in mutation
    };

    const handleResolve = async () => {
        if (!confirm('Resolve this exception?')) return;
        await dockMutations.resolveDockException(dock.id);
    };

    return (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-amber-200 dark:border-slate-700 p-4 mb-6">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-amber-900 dark:text-slate-50 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-500" />
                    Exception & Issues
                </h3>
                <div className="flex gap-2">
                    {hasActiveException && !isEditing && (
                        <button
                            onClick={handleResolve}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold px-2 py-1 rounded flex items-center gap-1"
                        >
                            <Check className="w-3 h-3" /> Resolve
                        </button>
                    )}
                    {!isEditing && (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="bg-white dark:bg-slate-800 border dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700 text-xs font-medium px-2 py-1 rounded dark:text-slate-200"
                        >
                            {hasActiveException ? 'Edit' : 'Add Note'}
                        </button>
                    )}
                </div>
            </div>

            {isEditing ? (
                <div className="space-y-3 bg-slate-50 dark:bg-slate-950 p-3 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm">
                    {/* Reason Select */}
                    <div>
                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Reason Code</label>
                        <select
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            className="w-full text-xs rounded border p-1.5 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200"
                        >
                            <option value="">-- Select Reason --</option>
                            <option value="Mechanical Issue">Mechanical Issue</option>
                            <option value="Spill / Safety">Spill / Safety</option>
                            <option value="IT / System Failure">IT / System Failure</option>
                            <option value="Staff Shortage">Staff Shortage</option>
                            <option value="Held for VIP">Held for VIP</option>
                            <option value="Other">Other</option>
                        </select>
                    </div>

                    {/* Owner Select */}
                    <div>
                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Assigned Owner</label>
                        <div className="flex items-center gap-2">
                            <UserPlus className="w-4 h-4 text-muted-foreground" />
                            <select
                                value={ownerId}
                                onChange={e => setOwnerId(e.target.value)}
                                className="w-full text-xs rounded border p-1.5 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200"
                            >
                                <option value="">-- Unassigned --</option>
                                {users.map((u: any) => (
                                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Note Textarea */}
                    <div>
                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block mb-1">Note</label>
                        <textarea
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            className="w-full text-xs rounded border p-2 min-h-[60px] dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200"
                            placeholder="Describe the issue..."
                        />
                    </div>

                    <div className="flex justify-end gap-2 pt-1">
                        <button onClick={() => setIsEditing(false)} className="text-xs px-3 py-1 rounded border hover:bg-gray-50 dark:border-slate-700 dark:hover:bg-slate-800 dark:text-slate-300">Cancel</button>
                        <button onClick={handleSave} className="text-xs px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 font-medium">Save</button>
                    </div>
                </div>
            ) : hasActiveException ? (
                <div className="space-y-2">
                    {dock.exceptionReason && (
                        <Badge variant="warning" className="text-[10px] mr-2">{dock.exceptionReason}</Badge>
                    )}
                    {dock.exceptionOwnerUserId && (
                        <Badge variant="info" className="text-[10px]">Owner: {dock.exceptionOwnerUserId}</Badge>
                    )}
                    {dock.exceptionNote && (
                        <div className="text-xs bg-slate-50 dark:bg-slate-950 p-2 rounded border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 italic">
                            "{dock.exceptionNote}"
                        </div>
                    )}
                    <div className="text-[10px] text-muted-foreground text-right dark:text-slate-400">
                        Last updated: {dock.exceptionUpdatedAt ? formatDistanceToNow(new Date(dock.exceptionUpdatedAt), { addSuffix: true }) : 'N/A'}
                    </div>
                </div>
            ) : (
                <div className="text-xs text-muted-foreground dark:text-slate-400 italic text-center py-2">
                    No active exceptions.
                </div>
            )}

            {/* History Toggle */}
            <div className="mt-4 pt-3 border-t border-amber-200/50 dark:border-slate-700/50">
                <button
                    onClick={() => setHistoryOpen(!historyOpen)}
                    className="flex items-center gap-2 text-[10px] font-bold text-amber-700 dark:text-slate-300 uppercase tracking-wider hover:text-amber-900 dark:hover:text-white"
                >
                    <History className="w-3 h-3" />
                    {historyOpen ? 'Hide History' : 'View History Log'}
                </button>

                {historyOpen && (
                    <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto pr-1">
                        {events.length === 0 ? <p className="text-[10px] text-muted-foreground italic">No events found.</p> :
                            events.map((ev: any) => (
                                <div key={ev.id} className="text-[11px] bg-white/50 dark:bg-slate-950/50 p-2 rounded border border-amber-100 dark:border-slate-800 flex flex-col gap-1">
                                    <div className="flex justify-between text-muted-foreground dark:text-slate-400 text-[10px]">
                                        <span className="font-bold">{ev.createdBy?.firstName} {ev.createdBy?.lastName || 'System'}</span>
                                        <span>{format(new Date(ev.createdAt), 'MMM d, HH:mm')}</span>
                                    </div>
                                    <div className="text-zinc-700 dark:text-slate-200">
                                        {ev.type === 'resolve' ? (
                                            <span className="text-emerald-600 dark:text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Resolved Exception</span>
                                        ) : (
                                            <span>
                                                {ev.payload.reason && <Badge variant="info" className="text-[9px] mr-1">{ev.payload.reason}</Badge>}
                                                {ev.payload.note}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                )}
            </div>
        </div>
    );
}

// Match backend Appointment snippet (minimal)
interface AppointmentSnippet {
    id: string;
    status: string;
    carrierName?: string;
    customerPoNumber?: string;
    startAt: string;
    checkInTime?: string;
    driverName?: string;
}

// Minimal Dock interface compatible with board
interface Dock {
    id: string;
    dockNumber: string;
    type?: string;
    status: string;
    now?: AppointmentSnippet | null;
    updatedAt?: string;
    // Exception Fields
    exceptionOwnerUserId?: string | null;
    exceptionReason?: string | null;
    exceptionNote?: string | null;
    exceptionResolvedAt?: string | null;
    exceptionUpdatedAt?: string | null;
}

interface DockScheduleDrawerProps {
    dockId: string | null;
    isOpen: boolean;
    onClose: () => void;
    dockNumber?: string;
    onActionComplete?: () => void;
    dock?: Dock; // Trusted canonical dock object from board
    // V1.3B: Quick action callbacks
    onStatusChange?: (dockId: string, status: string) => void;
    onEdit?: (dock: Dock) => void;
}

export function DockScheduleDrawer({ dockId, isOpen, onClose, dockNumber, onActionComplete, dock: boardDock, onStatusChange, onEdit }: DockScheduleDrawerProps) {
    const today = new Date().toISOString().split('T')[0];
    const [pendingAction, setPendingAction] = useState<string | null>(null);
    const [actionToConfirm, setActionToConfirm] = useState<{ id: string, action: string, context: string } | null>(null);
    const { token } = useAuthStore();

    // SWR Fetching (Standardized Key)
    const { data, error, isLoading } = useSWR(
        dockId && isOpen ? `${dockScheduleKey(dockId)}?date=${today}` : null,
        () => api.getDockSchedule(dockId!, today),
        {
            refreshInterval: 10000,
            revalidateOnFocus: true
        }
    );

    // V1.3C: Fetch blackouts for this dock
    const todayObj = new Date();
    const todayStart = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate()).toISOString();
    const todayEnd = new Date(todayObj.getFullYear(), todayObj.getMonth(), todayObj.getDate() + 1).toISOString();
    const { data: blackoutData } = useSWR<{ blackouts: BlackoutWindow[] }>(
        isOpen && dockId && token ? `/api/docks/blackouts?dockId=${dockId}&from=${todayStart}&to=${todayEnd}` : null,
        (url: string) => fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        { refreshInterval: 30000 }
    );
    const dockBlackouts = (blackoutData?.blackouts || []).filter(b =>
        new Date(b.endAt).getTime() > Date.now()
    );

    // Auto-close on route change (prevents stuck overlay)
    const [location] = useLocation();
    const prevLocationRef = useRef(location);

    useEffect(() => {
        if (prevLocationRef.current !== location) {
            prevLocationRef.current = location;
            if (isOpen) {
                onClose();
            }
        }
    }, [location, isOpen, onClose]);

    const appointments = data?.appointments || [];
    // Prioritize passed dock (canonical) over endpoint dock (potentially partial)
    const dock = boardDock || data?.dock;

    const executeAction = async (apptId: string, action: string) => {
        const actionKey = `${apptId}-${action}`;
        setPendingAction(actionKey);

        try {
            if (action === 'check_in') {
                await api.request(`/appointments/${apptId}/checkin`, { method: 'POST' });
            } else if (action === 'start') {
                await api.updateAppointmentStatus(apptId, 'in_progress');
            } else if (action === 'complete') {
                await api.request(`/appointments/${apptId}/checkout`, { method: 'POST' });
            } else if (action === 'cancel') {
                await api.request(`/appointments/${apptId}/cancel`, { method: 'POST' });
            }

            // Deterministic Refresh - AWAIT the refreshes to ensure UI sync
            if (dockId) {
                await refreshDockViews(dockId);
            }

            toast.success(`Action '${action.replace('_', ' ')}' completed`);

            if (onActionComplete) onActionComplete();
        } catch (error: any) {
            console.error('Lifecycle action failed', error);
                        if (action === 'complete') {
                showCenteredCheckoutError(error.message || `Failed to ${action.replace('_', ' ')}`);
            } else {
                toast.error(error.message || `Failed to ${action.replace('_', ' ')}`);
            }
            // Still refresh to ensure state is consistent (best effort)
            if (dockId) refreshDockViews(dockId);
        } finally {
            setPendingAction(null);
            setActionToConfirm(null);
        }
    };

    // Main Handler
    const handleLifecycleAction = async (apptId: string, action: 'check_in' | 'start' | 'complete' | 'cancel', contextInfo?: string) => {
        // Guard: Prevent double clicks
        if (pendingAction) return;

        // Confirmation Logic
        if (['cancel', 'complete'].includes(action)) {
            setActionToConfirm({ id: apptId, action, context: contextInfo || 'this appointment' });
            return;
        }

        // Direct Execution
        await executeAction(apptId, action);
    };

    const confirmAction = () => {
        if (!actionToConfirm) return;
        executeAction(actionToConfirm.id, actionToConfirm.action);
    };

    // Organized Schedule Sections
    const { now, next, approved, completed, cancelled } = useMemo(() => {
        const now: any[] = [];
        const next: any[] = [];
        const approved: any[] = [];
        const completed: any[] = [];
        const cancelled: any[] = [];

        const sorted = [...appointments].sort((a: any, b: any) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());

        // 1. Determine "NOW" (Active Load)
        // Priority: Use Board Canonical 'now' if available
        if (dock?.now) {
            now.push(dock.now);
        } else {
            // Fallback: Scan schedule list if we don't have a trusted 'dock.now'
            // We only fallback if we have *no* board dock info, to avoid flicker
            if (!boardDock) {
                const activeIndex = sorted.findIndex((a: any) => ['checked_in', 'in_progress'].includes(a.status));
                if (activeIndex !== -1) {
                    now.push(sorted[activeIndex]);
                }
            }
        }

        const nowIds = new Set(now.map(n => n.id));

        // 2. Determine Next/Approved (Skipping what is already in NOW)
        const candidates = sorted.filter((a: any) => a.status === 'approved' && !nowIds.has(a.id));

        candidates.forEach((appt: any, idx: number) => {
            if (idx === 0 && now.length === 0) {
                // If nothing is NOW, first approved is NEXT
                next.push(appt);
            } else if (idx === 0 && now.length > 0) {
                // If something is NOW, first approved is NEXT
                next.push(appt);
            } else {
                approved.push(appt);
            }
        });

        // 3. Completed / Cancelled
        sorted.forEach((a: any) => {
            if (nowIds.has(a.id)) return; // Skip if in NOW
            if (a.status === 'completed') completed.push(a);
            if (['cancelled', 'rejected'].includes(a.status)) cancelled.push(a);
        });

        return { now, next, approved, completed, cancelled };
    }, [appointments, dock, boardDock]);

    // Header Data
    const systemStatus = dock ? getDockDisplayStatus(dock) : 'Loading...';
    const manualStatus = dock ? toManualSelectValue(dock.status) : '';
    const isOverride = dock && systemStatus !== manualStatus && manualStatus !== 'Maintenance' && manualStatus !== 'Out of Service';
    const isMaintenance = systemStatus === 'Maintenance';
    const isOOS = systemStatus === 'Out of Service';

    const getDwellDisplay = (checkInTime?: string) => {
        if (!checkInTime) return null;
        const checkIn = new Date(checkInTime);
        const now = new Date();
        const diffMinutes = Math.floor((now.getTime() - checkIn.getTime()) / 60000);
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;

        // Dark-mode friendly colors (using bg-opacity and text-color)
        let colorClass = "bg-emerald-500/10 text-emerald-400";
        if (diffMinutes > 120) colorClass = "bg-amber-500/10 text-amber-400";
        if (diffMinutes > 240) colorClass = "bg-red-500/10 text-red-400 animate-pulse";

        return (
            <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono font-medium ${colorClass}`}>
                {hours}h {minutes}m
            </span>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
            <DialogPortal>
                <DialogOverlay />
                <DialogContent className="max-w-2xl w-full max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
                {/* Header Strip */}
                <div className="bg-muted/40 border-b p-6 pb-4 shrink-0">
                    <div className="flex items-start justify-between mb-4">
                        <div>
                            <DialogTitle className="text-3xl font-bold font-mono tracking-tight flex items-center gap-3">
                                Dock {dockNumber || dock?.dockNumber}
                            </DialogTitle>
                            <DialogDescription className="mt-1 flex items-center gap-2">
                                <CalendarDays className="w-3.5 h-3.5" />
                                {format(new Date(), 'EEEE, MMMM d')}
                            </DialogDescription>
                        </div>
                        <div className="flex items-start gap-3">
                        <div className="flex flex-col items-end gap-1.5">
                            <div className={`px-2.5 py-1 rounded text-xs font-bold uppercase tracking-wider border ${systemStatus === 'Occupied' ? 'bg-cyan-500/10 text-cyan-600 border-cyan-500/20' :
                                systemStatus === 'Maintenance' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                                    systemStatus === 'Out of Service' ? 'bg-red-500/10 text-red-600 border-red-500/20' :
                                        'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                }`}>
                                {systemStatus}
                            </div>
                            {isOverride && (
                                <div className="text-[10px] text-blue-500 font-medium flex items-center gap-1 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                                    <span>⚡ Priority Override</span>
                                </div>
                            )}
                            <div className="text-[10px] text-muted-foreground/60 font-mono">
                                Updated: {dock?.updatedAt ? format(new Date(dock.updatedAt), 'HH:mm') : '--:--'}
                            </div>
                        </div>
                        {/* Close X button */}
                        <button
                            onClick={onClose}
                            className="p-1.5 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 -mt-1"
                            title="Close"
                        >
                            <X className="w-5 h-5" />
                        </button>
                        </div>
                    </div>

                    {/* Quick Actions Bar */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {onEdit && dock && (
                            <button
                                onClick={() => { onEdit(dock); onClose(); }}
                                className="text-xs px-3 py-1.5 rounded-md border bg-background hover:bg-accent transition-colors flex items-center gap-1.5 font-medium"
                            >
                                <Settings className="w-3 h-3" /> Edit Dock
                            </button>
                        )}
                        {onStatusChange && dockId && !isMaintenance && !isOOS && (
                            <button
                                onClick={() => { onStatusChange(dockId, 'Maintenance'); onClose(); }}
                                className="text-xs px-3 py-1.5 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400 transition-colors flex items-center gap-1.5 font-medium"
                            >
                                <Wrench className="w-3 h-3" /> Set Maintenance
                            </button>
                        )}
                        {onStatusChange && dockId && !isOOS && !isMaintenance && (
                            <button
                                onClick={() => { onStatusChange(dockId, 'Out of Service'); onClose(); }}
                                className="text-xs px-3 py-1.5 rounded-md border border-red-200 dark:border-red-800 bg-background hover:bg-red-50 dark:hover:bg-red-900/20 text-red-500 transition-colors flex items-center gap-1.5 font-medium"
                            >
                                <XCircle className="w-3 h-3" /> Set Out of Service
                            </button>
                        )}
                        {onStatusChange && dockId && (isMaintenance || isOOS) && (
                            <button
                                onClick={() => { onStatusChange(dockId, 'Available'); onClose(); }}
                                className="text-xs px-3 py-1.5 rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 transition-colors flex items-center gap-1.5 font-medium"
                            >
                                <ChevronDown className="w-3 h-3" /> Set Available
                            </button>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {isLoading ? (
                        <div className="flex h-40 items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                        </div>
                    ) : error ? (
                        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm border border-destructive/20">
                            Failed to load schedule.
                        </div>
                    ) : (
                        <>
                            {/* EXCEPTION Management */}
                            {dock && <ExceptionSection dock={dock} />}

                            {/* V1.3C: Blackout warning cards */}
                            {dockBlackouts.length > 0 && (
                                <div className="mb-3 space-y-2">
                                    {dockBlackouts.map(b => {
                                        const isActive = new Date(b.startAt).getTime() <= Date.now() && new Date(b.endAt).getTime() > Date.now();
                                        const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                                        return (
                                            <div
                                                key={b.id}
                                                className={`flex items-start gap-3 px-3 py-2.5 rounded-lg border ${
                                                    isActive
                                                        ? 'bg-red-50/80 dark:bg-red-950/20 border-red-200 dark:border-red-800/50'
                                                        : 'bg-amber-50/60 dark:bg-amber-950/15 border-amber-200 dark:border-amber-800/40'
                                                }`}
                                            >
                                                <ShieldOff className={`w-4 h-4 mt-0.5 shrink-0 ${isActive ? 'text-red-500' : 'text-amber-500'}`} />
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`text-xs font-bold ${
                                                            isActive ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'
                                                        }`}>
                                                            {isActive ? 'Blocked Now' : 'Upcoming Block'}
                                                        </span>
                                                        <span className="text-xs text-slate-600 dark:text-slate-400">{b.reason}</span>
                                                    </div>
                                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 tabular-nums mt-0.5">
                                                        {fmtTime(b.startAt)} – {fmtTime(b.endAt)}
                                                        {b.isWarehouseWide && <span className="ml-1.5 text-red-500 dark:text-red-400 font-bold">· All Docks</span>}
                                                    </div>
                                                    <a
                                                        href="/dock-management"
                                                        className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-1 inline-block"
                                                    >
                                                        Manage in Dock Operations →
                                                    </a>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* NOW SECTION */}
                            <section>
                                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></span>
                                    Now Processing
                                </h3>
                                {now.length > 0 ? now.map((appt: any) => (
                                    <div key={appt.id} className="bg-card rounded-xl border-l-4 border-l-blue-500 border border-t-border/50 border-r-border/50 border-b-border/50 shadow-sm p-4 relative overflow-hidden">
                                        <div className="absolute top-0 right-0 p-2 opacity-5">
                                            <Truck className="w-24 h-24" />
                                        </div>
                                        <div className="relative z-10">
                                            <div className="flex justify-between items-start mb-3">
                                                <div>
                                                    <h4 className="text-lg font-bold">{appt.carrierName}</h4>
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <span className="font-mono bg-secondary px-1.5 rounded text-xs">PO: {appt.customerPoNumber}</span>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-2xl font-mono font-bold tracking-tight">
                                                        {format(new Date(appt.startAt), 'HH:mm')}
                                                    </div>
                                                    {getDwellDisplay(appt.checkInTime)}
                                                </div>
                                            </div>

                                            {/* Primary Action Bar */}
                                            <div className="grid grid-cols-2 gap-2 mt-4">
                                                {appt.status === 'checked_in' && (
                                                    <button
                                                        onClick={() => handleLifecycleAction(appt.id, 'start')}
                                                        disabled={!!pendingAction}
                                                        className="col-span-2 flex items-center justify-center gap-2 py-2 text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {pendingAction === `${appt.id}-start` ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                                                        Start Loading
                                                    </button>
                                                )}
                                                {appt.status === 'in_progress' && (
                                                    <button
                                                        onClick={() => handleLifecycleAction(appt.id, 'complete', `${appt.carrierName} (PO: ${appt.customerPoNumber})`)}
                                                        disabled={!!pendingAction}
                                                        className="col-span-2 flex items-center justify-center gap-2 py-2 text-sm font-medium bg-zinc-900 hover:bg-zinc-800 text-white rounded-md shadow-sm transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {pendingAction === `${appt.id}-complete` ? <Loader2 className="w-4 h-4 animate-spin" /> : <StopCircle className="w-4 h-4" />}
                                                        Complete Job
                                                    </button>
                                                )}
                                                {/* Fallback if status isn't actionable (e.g. unknown state) */}
                                                {!['checked_in', 'in_progress'].includes(appt.status) && (
                                                    <div className="col-span-2 text-center text-xs text-muted-foreground italic py-2">
                                                        Status: {appt.status}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-sm text-muted-foreground italic p-4 border rounded-lg border-dashed bg-muted/20 text-center">
                                        No active load in progress.
                                    </div>
                                )}
                            </section>

                            {/* NEXT SECTION */}
                            <section>
                                <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                                    Next Up
                                </h3>
                                {next.length > 0 ? next.map((appt: any) => {
                                    // Correct LATE logic: Only if approved and past grace period
                                    const isLate = appt.status === 'approved' && isPast(addMinutes(new Date(appt.startAt), 15));
                                    return (
                                        <div key={appt.id} className="bg-card rounded-xl border p-4 shadow-sm group hover:border-emerald-500/50 transition-colors">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-600 flex items-center justify-center font-bold">
                                                        {format(new Date(appt.startAt), 'HH')}
                                                        <span className="text-[10px] items-start flex -mt-2">:</span>
                                                        {format(new Date(appt.startAt), 'mm')}
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold">{appt.carrierName}</h4>
                                                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                            <span>PO: {appt.customerPoNumber}</span>
                                                            {isLate && <Badge variant="error" className="h-4 px-1 text-[9px]">LATE</Badge>}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button
                                                        onClick={() => handleLifecycleAction(appt.id, 'check_in')}
                                                        disabled={!!pendingAction}
                                                        title="Check In"
                                                        className="p-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {pendingAction === `${appt.id}-check_in` ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                                    </button>
                                                    
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }) : (
                                    <div className="text-sm text-muted-foreground italic pl-1">Nothing scheduled next.</div>
                                )}
                            </section>

                            {/* TODAY / REST OF DAY */}
                            {(approved.length > 0 || completed.length > 0 || cancelled.length > 0) && (
                                <section className="pt-4 border-t">
                                    <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">Today's Timeline</h3>
                                    <div className="space-y-4 pl-3 border-l-2 border-muted ml-2">
                                        {approved.map((appt: any) => (
                                            <div key={appt.id} className="pl-6 relative">
                                                <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full border-2 border-background bg-zinc-300"></span>
                                                <div className="flex justify-between items-center bg-card/50 p-3 rounded-lg border border-dashed">
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-sm font-medium">{format(new Date(appt.startAt), 'HH:mm')}</span>
                                                        <div className="flex flex-col">
                                                            <span className="text-sm font-medium">{appt.carrierName}</span>
                                                            <span className="text-xs text-muted-foreground">PO: {appt.customerPoNumber}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-1">
                                                        <button
                                                            onClick={() => handleLifecycleAction(appt.id, 'cancel', `${appt.carrierName}`)}
                                                            className="text-xs text-muted-foreground hover:text-red-600 px-2 py-1 rounded hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            disabled={!!pendingAction}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Completed/Cancelled Sections (Read Only) */}
                                        {completed.map((appt: any) => (
                                            <div key={appt.id} className="pl-6 relative opacity-60">
                                                <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full border-2 border-background bg-zinc-800"></span>
                                                <div className="flex justify-between items-center p-2">
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-sm line-through text-muted-foreground">{format(new Date(appt.startAt), 'HH:mm')}</span>
                                                        <span className="text-sm text-muted-foreground">{appt.carrierName} <span className="text-[10px]">(Completed)</span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}

                                        {cancelled.map((appt: any) => (
                                            <div key={appt.id} className="pl-6 relative opacity-40">
                                                <span className="absolute -left-[7px] top-2 w-3 h-3 rounded-full border-2 border-background bg-red-300"></span>
                                                <div className="flex justify-between items-center p-2">
                                                    <div className="flex items-center gap-3">
                                                        <span className="font-mono text-sm line-through text-red-400">{format(new Date(appt.startAt), 'HH:mm')}</span>
                                                        <span className="text-sm text-red-400">{appt.carrierName} <span className="text-[10px]">(Cancelled)</span></span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}
                        </>
                    )}
                </div>

                {/* Confirmation Dialog */}
                <AlertDialog open={!!actionToConfirm} onOpenChange={(open) => !open && setActionToConfirm(null)}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Confirm Action</AlertDialogTitle>
                            <AlertDialogDescription>
                                Are you sure you want to <strong>{actionToConfirm?.action?.replace('_', ' ')}</strong>?
                                <br /><br />
                                Context: <span className="font-mono text-xs bg-muted p-1 rounded">{actionToConfirm?.context}</span>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={confirmAction} className={
                                actionToConfirm?.action === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''
                            }>
                                Confirm
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}




